import { jwtDecode } from 'jwt-decode';

interface JwtPayload {
  sub: string;
  jti: string;
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier': string;
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress': string;
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name': string;
  roles: string[];
  permissions: string[];
  AdminName: string;
  AdminId: string;
  exp: number;
  iss: string;
  aud: string;
  ProfilePictureFileId: string;
}

// ============================================================================
// TOKEN STORAGE UTILITIES
// Centralized functions for managing tokens in sessionStorage and localStorage
// ============================================================================

/**
 * Check if currently impersonating another user
 */
export const isImpersonating = (): boolean => {
  return sessionStorage.getItem('is_impersonating') === 'true';
};

/**
 * Get the current access token (prioritizing sessionStorage for impersonation)
 */
export const getToken = (): string | null => {
  // Check sessionStorage first (impersonated token, tab-specific)
  const impersonatedToken = sessionStorage.getItem('impersonated_token');
  if (impersonatedToken) {
    return impersonatedToken;
  }
  // Fall back to localStorage (normal token, shared across tabs)
  return localStorage.getItem('token');
};

/**
 * Get the current refresh token (prioritizing sessionStorage for impersonation)
 */
export const getRefreshToken = (): string | null => {
  // Check sessionStorage first (impersonated refresh token, tab-specific)
  const impersonatedRefreshToken = sessionStorage.getItem('impersonated_refresh_token');
  if (impersonatedRefreshToken) {
    return impersonatedRefreshToken;
  }
  // Fall back to localStorage (normal refresh token, shared across tabs)
  return localStorage.getItem('refresh_token');
};

/**
 * Set tokens in the appropriate storage based on impersonation state
 * @param accessToken - The access token to store
 * @param refreshToken - The refresh token to store
 * @param impersonating - Whether this is an impersonation session (defaults to checking current state)
 */
export const setTokens = (
  accessToken: string | null,
  refreshToken: string | null,
  impersonating: boolean = isImpersonating()
): void => {
  if (impersonating) {
    // Store in sessionStorage for impersonation (tab-specific)
    if (accessToken) sessionStorage.setItem('impersonated_token', accessToken);
    if (refreshToken) sessionStorage.setItem('impersonated_refresh_token', refreshToken);
  } else {
    // Store in localStorage for normal auth (shared across tabs)
    if (accessToken) localStorage.setItem('token', accessToken);
    if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
  }
};

/**
 * Set impersonation state and tokens
 * @param accessToken - The impersonated user's access token
 * @param refreshToken - The impersonated user's refresh token
 * @param adminId - The admin user's ID
 */
export const setImpersonationTokens = (
  accessToken: string,
  refreshToken: string,
  adminId: string
): void => {
  sessionStorage.setItem('impersonated_token', accessToken);
  sessionStorage.setItem('impersonated_refresh_token', refreshToken);
  sessionStorage.setItem('is_impersonating', 'true');
  sessionStorage.setItem('admin_id', adminId);
};

/**
 * Clear impersonation state and tokens, restore admin tokens
 * @param adminAccessToken - The admin's access token to restore
 * @param adminRefreshToken - The admin's refresh token to restore
 */
export const clearImpersonationTokens = (
  adminAccessToken?: string,
  adminRefreshToken?: string
): void => {
  // Clear impersonation state from sessionStorage
  sessionStorage.removeItem('impersonated_token');
  sessionStorage.removeItem('impersonated_refresh_token');
  sessionStorage.removeItem('is_impersonating');
  sessionStorage.removeItem('admin_id');

  // Restore admin tokens if provided
  if (adminAccessToken) localStorage.setItem('token', adminAccessToken);
  if (adminRefreshToken) localStorage.setItem('refresh_token', adminRefreshToken);
};

/**
 * Clear all tokens from both storages (for logout)
 */
export const clearAllTokens = (): void => {
  // Clear localStorage
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('profilePicture');

  // Clear sessionStorage impersonation
  sessionStorage.removeItem('impersonated_token');
  sessionStorage.removeItem('impersonated_refresh_token');
  sessionStorage.removeItem('is_impersonating');
  sessionStorage.removeItem('admin_id');
};

// ============================================================================
// TOKEN DECODING AND CLAIMS
// ============================================================================

const decodeToken = (): JwtPayload | null => {
  const token = getToken();
  if (token) {
    return jwtDecode<JwtPayload>(token);
  }

  return null;
};

export const getUserName = (): string | null => {
  const decodedToken = decodeToken();

  return decodedToken
    ? decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name']
    : null;
};

export const getUserEmail = (): string | null => {
  const decodedToken = decodeToken();

  return decodedToken
    ? decodedToken[
        'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'
      ]
    : null;
};

export const getUserId = (): string | null => {
  const decodedToken = decodeToken();

  return decodedToken
    ? decodedToken[
        'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'
      ]
    : null;
};

export const getAdminId = (): string | null => {
  const decodedToken = decodeToken();

  return decodedToken ? decodedToken['AdminId'] : null;
};

export const getAdminUserName = (): string | null => {
  const decodedToken = decodeToken();

  return decodedToken ? decodedToken['AdminName'] : null;
};

/**
 * Returns the current user's profile picture FileRecord ID (not a usable URL).
 * Resolve it to a displayable URL via useFileApi().getFileUrl(fileId).
 */
export const getProfilePictureFileId = (): string | null => {
  const decodedToken = decodeToken();
  return decodedToken?.ProfilePictureFileId || null;
};

export const getRoles = (): string[] | null => {
  const decodedToken = decodeToken();
  if (decodedToken) {
    const roles = decodedToken.roles;

    if (!roles) {
      return null;
    }

    // Handle case where roles might be stringified JSON
    if (typeof roles === 'string') {
      try {
        // Try to parse if it's a stringified array
        const parsedRoles = JSON.parse(roles);
        return Array.isArray(parsedRoles) ? parsedRoles : [roles];
      } catch {
        // If parsing fails, treat it as a single role string
        return [roles];
      }
    }

    // If roles is already an array, return it
    if (Array.isArray(roles)) {
      return roles;
    }

    // If roles is neither a string nor an array, return it as a single-item array
    return [String(roles)];
  }

  return null;
};

export const getPermissions = (): string[] | null => {
  const decodedToken = decodeToken();
  if (decodedToken) {
    const permissions = decodedToken.permissions;

    if (!permissions) {
      return null;
    }

    // Handle case where permissions might be stringified JSON
    if (typeof permissions === 'string') {
      try {
        // Try to parse if it's a stringified array
        const parsedPermissions = JSON.parse(permissions);
        return Array.isArray(parsedPermissions)
          ? parsedPermissions
          : [permissions];
      } catch {
        // If parsing fails, treat it as a single permission string
        return [permissions];
      }
    }

    // If permissions is already an array, return it
    if (Array.isArray(permissions)) {
      return permissions;
    }

    // If permissions is neither a string nor an array, return it as a single-item array
    return [String(permissions)];
  }

  return null;
};

/**
 * Check if the current token is expired.
 * @returns true if token is expired or invalid, false otherwise
 */
export const isTokenExpired = (): boolean => {
  const decodedToken = decodeToken();

  if (!decodedToken || !decodedToken.exp) {
    return true; // No token or no expiration claim means expired
  }

  const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
  return decodedToken.exp < currentTime;
};

/**
 * Check if the token will expire within the specified threshold.
 * @param thresholdSeconds - Number of seconds before expiration to consider as "expiring soon" (default: 60)
 * @returns true if token will expire within threshold, false otherwise
 */
export const isTokenExpiringSoon = (thresholdSeconds: number = 60): boolean => {
  const decodedToken = decodeToken();

  if (!decodedToken || !decodedToken.exp) {
    return true; // No token or no expiration claim
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const expirationTime = decodedToken.exp;
  const timeUntilExpiry = expirationTime - currentTime;

  return timeUntilExpiry <= thresholdSeconds;
};

/**
 * Get the number of seconds until token expiration.
 * @returns Number of seconds until expiration, or 0 if expired/invalid
 */
export const getSecondsUntilExpiry = (): number => {
  const decodedToken = decodeToken();

  if (!decodedToken || !decodedToken.exp) {
    return 0;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const timeUntilExpiry = decodedToken.exp - currentTime;

  return Math.max(0, timeUntilExpiry);
};
